// AuthLogin.tsx

import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode'; // Asegúrate de importar correctamente jwtDecode
import { getUserData } from '../api/userApi'; // Asegúrate de tener esta función

type UserRole = 'admin' | 'cliente' | 'tecnico';

interface UserData {
  nombre: string;
  emailnotificaciones: string;
  recibircorreos: boolean;
  profilepic: string | null;
  id_usuario_padre: string | null;
}

interface AuthContextType {
  isAuthenticated: boolean;
  userRole: UserRole | null;
  userName: string | null;
  userId: string | null;
  idUsuarioPadre: string | null;
  userData: UserData | null; // Nuevo campo para almacenar todos los datos del usuario
  isLoading: boolean;
  login: (token: string, name: string) => Promise<void>; // Actualizado para ser asíncrono
  logout: () => void;
  updateUserName: (newName: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken');
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [idUsuarioPadre, setIdUsuarioPadre] = useState<string | null>(null);
  const [userData, setUserDataState] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Función para obtener y establecer datos del usuario
  const fetchAndSetUserData = async () => {
    try {
      const data = await getUserData();
      const userData: UserData = {
        nombre: data.nombre,
        emailnotificaciones: data.emailnotificaciones,
        recibircorreos: data.recibircorreos,
        profilepic: data.profilepic,
        id_usuario_padre: data.id_usuario_padre,
      };
      setUserDataState(userData);
      setIdUsuarioPadre(data.id_usuario_padre);
    } catch (error) {
      console.error("Error fetching user data:", error);
      logout(); // Si falla la obtención de datos, cerrar sesión
    }
  };

  // Función de login, actualizada para ser asíncrona
  const login = async (token: string, name: string) => {
    const decodedToken: any = jwtDecode(token);
    const role: UserRole = decodedToken.role;
    const id: string = decodedToken.userId;

    setIsAuthenticated(true);
    setUserRole(role);
    setUserName(name);
    setUserId(id);
    localStorage.setItem('authToken', token);
    localStorage.setItem('userName', name);
    localStorage.setItem('userId', id);

    // Obtener y establecer datos del usuario después de iniciar sesión
    await fetchAndSetUserData();
  };

  // Función para actualizar el nombre del usuario en el contexto
  const updateUserName = (newName: string) => {
    setUserName(newName);
    localStorage.setItem('userName', newName);
  };

  // Función de logout
  const logout = () => {
    setIsAuthenticated(false);
    setUserRole(null);
    setUserName(null);
    setUserId(null);
    setIdUsuarioPadre(null);
    setUserDataState(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('userName');
    localStorage.removeItem('userId');
  };

  // Efecto para cargar datos del usuario al montar el proveedor
  useEffect(() => {
    const loadUserData = async () => {
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          const decodedToken: any = jwtDecode(token);
          setIsAuthenticated(true);
          setUserRole(decodedToken.role);
          setUserName(localStorage.getItem('userName'));
          setUserId(decodedToken.userId);

          // Obtener y establecer datos adicionales del usuario
          await fetchAndSetUserData();
        } catch (error) {
          console.error("Error decoding token or fetching user data:", error);
          logout();
        }
      }
      setIsLoading(false);
    };
    loadUserData();
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, userRole, userName, userId, idUsuarioPadre, userData, isLoading, login, logout, updateUserName }}>
      {children}
    </AuthContext.Provider>
  );
};
